import React from 'react';
import { useTranslation } from 'react-i18next';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faArrowsLeftRightToLine,
  faCalendarAlt,
  faCheck,
  faCircleExclamation,
  faSnowflake,
  faUpRightAndDownLeftFromCenter,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { Divider } from 'primereact/divider';
import { CostSimulatorFormState } from './CostSimulatorExport';
import { CostSimulatorImportFormState } from './CostSimulatorImport';
import styles from './CostSimulatorResults.module.scss';

interface ICostSimulatorResults {
  data:
    | {
        results: string | null | undefined;
        formDataImport?: CostSimulatorImportFormState;
        formDataExport?: CostSimulatorFormState;
        total: number;
      }
    | undefined;
  isSliderOpen: boolean;
  isImport?: boolean;
}

export interface ResultItem {
  description: string;
  summary?: string | null;
  amount: number;
  details?: string | null;
  secondaryDetails?: string | null;
}

export const parseProformaDetails = (
  apiResponse: string | undefined | null
) => {
  let details;
  let total;

  const removeFirstUS = (input: string): string => {
    return input.replace(/U\$S/, '').trim();
  };

  if (!apiResponse) {
    return { details: [], total: 0 };
  }

  if (apiResponse) {
    const lines = apiResponse
      .split('\r\n')
      .filter(
        (line) =>
          line.trim() !== '' &&
          !line.trim().startsWith('TOTAL') &&
          !line.trim().startsWith('CONTENEDOR')
      );

    const groupedLines: string[] = [];
    let i = 0;
    while (i < lines.length) {
      if (lines[i]?.startsWith('Almacenaje') && lines[i + 1] && lines[i + 2]) {
        if (lines[i + 2].includes('U$S')) {
          groupedLines.push(
            `${lines[i]} <br /> <span class=${styles.fontWeightLight}>${removeFirstUS(lines[i + 1])}</span>`
          );
          groupedLines.push(
            `${lines[i + 2]} <br /> <span class=${styles.fontWeightLight}>${removeFirstUS(lines[i + 2])}</span>`
          );
          i += 3;
        }
        if (!lines[i + 2].includes('U$S')) {
          groupedLines.push(
            `${lines[i]} <br /> <span class=${styles.fontWeightLight}>${removeFirstUS(lines[i + 1])}</span>`
          );
          i += 2;
        }
      } else if (
        (lines[i]?.startsWith('Suministro') ||
          lines[i]?.startsWith('Recargo-Almacenaje') ||
          lines[i]?.startsWith('Segregación')) &&
        lines[i + 1]
      ) {
        groupedLines.push(
          `${lines[i]} <br /> <span class=${styles.fontWeightLight}>${removeFirstUS(lines[i + 1])}</span>`
        );
        i += 2;
      } else if (lines[i + 1]?.startsWith('días a')) {
        groupedLines.push(
          `${lines[i]} <br /> <span class=${styles.fontWeightLight}>${removeFirstUS(lines[i + 1])}</span>`
        );
        i += 2;
      } else {
        groupedLines.push(lines[i]);
        i += 1;
      }
    }
    details = groupedLines.map((groupedLine: string) => {
      const usMatches = groupedLine.match(/U\$S\s*([\d.,]+)/g);
      const usValues = usMatches
        ? usMatches.map((match) =>
            match
              .replace(/U\$S\s*/, '')
              .replace(',', '.')
              .trim()
          )
        : [];

      const [description, amountPart, third] = groupedLine
        .split(/U\$S/)
        .map((part) => part.trim());

      let amountString;
      let secondaryAmountString;
      if (
        groupedLine.includes('Almacenaje') ||
        groupedLine.includes('Recargo por ingreso anticipado')
      ) {
        amountString = amountPart
          ? amountPart.split(' ')[0].replace(',', '.')
          : '0';
        secondaryAmountString = third
          ? third.split(' ')[0].replace(',', '.')
          : null;
      } else if (groupedLine.includes('días a') && usValues.length >= 2) {
        amountString = usValues[1] || '0';
        secondaryAmountString = null;
      } else {
        amountString = amountPart
          ? amountPart.split(' ')[0].replace(',', '.')
          : '0';
      }

      const amount = parseFloat(amountString);
      let secondaryAmount = null;
      if (secondaryAmountString) {
        secondaryAmount = Number.isNaN(parseFloat(secondaryAmountString))
          ? 0
          : parseFloat(secondaryAmountString);
      }
      const groupedParsed = groupedLine.replaceAll('días a', 'días a U$S');
      const str = groupedParsed
        .split('U$S')
        .filter((x, n, self) => self.indexOf(x) === n)
        .join('')
        .replaceAll('días a', 'días a U$S');

      const result = str.replace(/U\$S\s*\d+,\d+\s+\d+,\d+/g, (match) =>
        match.replace(/\s+\d+,\d+$/, '')
      );

      let displayKey;
      if (groupedLine.startsWith('Almacenaje')) {
        displayKey = result.replaceAll('días a', 'días a');
      } else if (groupedLine.includes('días a') && usValues.length >= 2) {
        const beforeBr = groupedLine.split('<br')[0].trim();
        if (beforeBr.includes('U$S') && beforeBr.match(/U\$S/g)?.length === 2) {
          const usMatchesArray = Array.from(beforeBr.matchAll(/U\$S/g));
          if (usMatchesArray.length >= 2) {
            const secondUSIndex = usMatchesArray[1].index;
            displayKey = beforeBr.substring(0, secondUSIndex).trim();
          } else {
            displayKey = beforeBr;
          }
        } else {
          const rate = usValues[0]?.replace('.', ',') || '';
          const daysMatch = description.match(/(\d+)\s+días\s+a/);
          const days = daysMatch ? daysMatch[1] : '';
          displayKey = `${days} días a U$S ${rate}`;
        }
      } else {
        displayKey = description.replace(/días a/, 'días a');
      }

      return {
        key: displayKey,
        amount: Number.isNaN(amount) ? 0 : amount,
        secondaryAmount,
      };
    });

    const totalLinesS = apiResponse
      .split('\r\n')
      .filter((line: string) => line.trim() !== '');

    const totalLines = totalLinesS.find((line: string) =>
      line.includes('TOTAL')
    );
    total = 0;
    if (totalLines) {
      const totalParts = totalLines?.split('TOTAL');
      const totalString =
        totalParts && totalParts.length > 1 ? totalParts[1].trim() : '0';
      total = totalString.split('U$S');
    }
  }
  return { details, total };
};

export const CostSimulatorResults: React.FC<ICostSimulatorResults> = ({
  data,
  isSliderOpen,
  isImport,
}) => {
  const { t } = useTranslation('costSimulatorForm');

  const dataFormForTags = isImport
    ? data?.formDataImport
    : data?.formDataExport;

  const sortedDataFormTags =
    dataFormForTags &&
    Object.entries(dataFormForTags).sort((a, b) => {
      return Number(b[1]) - Number(a[1]);
    });

  const buttonLabels: {
    [key: string]: {
      label: string;
      icon?: IconProp;
      show?: boolean;
      isDisabled?: boolean;
    };
  } = isImport
    ? {
        tamano: {
          label: `${data?.formDataImport?.tamano} ${t('feet')}`,
          icon: faArrowsLeftRightToLine,
          show: true,
        },
        entregaDirecta: {
          label: t('directDelivery'),
          icon: faCheck,
          show: true,
        },
        dias: {
          label: `${data?.formDataImport?.dias}  ${t('days')}`,
          icon: faCalendarAlt,
          show: !data?.formDataImport?.entregaDirecta,
        },
        esIMDG: {
          label: `IMDG`,
          icon: faCircleExclamation,
          isDisabled: data?.formDataImport?.entregaDirecta,
          show: true,
        },
        esOOG: {
          label: 'OGG',
          icon: faUpRightAndDownLeftFromCenter,
          show: true,
          isDisabled: false,
        },
        esReeferOperativo: {
          label: t('operativeReefer'),
          icon: faSnowflake,
          show: data?.formDataImport?.entregaDirecta,
          isDisabled: data?.formDataImport?.entregaDirecta,
        },
      }
    : {
        ventanaIngreso: {
          label: t('window'),
          icon: faArrowsLeftRightToLine,
          show: data?.formDataExport?.ventanaIngreso,
        },
        tamanioContenedor: {
          label: `${data?.formDataExport?.tamanioContenedor} ${t('feet')}`,
          icon: faArrowsLeftRightToLine,
          show: true,
        },
        cobraEarlyArrival: {
          label: `${t('EAR')} | ${data?.formDataExport?.cntDiasFueraVentana}  ${t('days')}`,
          icon: faCalendarAlt,
          show: data?.formDataExport?.cobraEarlyArrival ?? false,
        },
        cobraLateArrival: {
          label: `${t('LAR')}`,
          icon: faCalendarAlt,
          show: data?.formDataExport?.cobraLateArrival ?? false,
        },
        esIMO: {
          label: `IMDG | ${data?.formDataExport?.cntDiasAntesComOP ? Number(data?.formDataExport?.cntDiasAntesComOP) + 1 : 1} ${t('days')}`,
          icon: faCircleExclamation,
          show: true,
        },
        esOOG: {
          label: 'OGG',
          icon: faUpRightAndDownLeftFromCenter,
          show: true,
        },
        esReefer: {
          label: `${t('operativeReefer')} | ${data?.formDataExport?.cntDiasAntesCierreStacking && !data?.formDataExport?.cobraLateArrival ? Number(data?.formDataExport?.cntDiasAntesCierreStacking) + 1 : 1} ${t('days')}`,
          icon: faSnowflake,
          show: true,
        },
      };

  const { details, total } = parseProformaDetails(data?.results);

  return (
    <div className={isSliderOpen ? styles.sliderOpen : styles.sliderClosed}>
      <Divider layout="vertical" className={styles.divider} />
      <h4>{t('calcResults')}</h4>
      <div className={styles.tagContainer}>
        {sortedDataFormTags?.map(([key, value]) => {
          if (buttonLabels[key]?.show) {
            const isDisabled = !value;

            return (
              <div
                key={key}
                className={classNames(
                  styles.outlinedTag,
                  isDisabled && styles.disabled
                )}
              >
                {buttonLabels[key].icon && (
                  <FontAwesomeIcon
                    icon={buttonLabels[key].icon as IconProp}
                    className={styles.icon}
                  />
                )}
                {buttonLabels[key].label}
              </div>
            );
          }
          return null;
        })}
      </div>
      <ul
        className={`${styles.list} ${styles.listAddProforma} ${styles.detailProforma}`}
      >
        {details?.map(
          (result) =>
            result && (
              <React.Fragment key={`${result?.key}`}>
                <li>
                  <span
                    /* eslint-disable-next-line react/no-danger */
                    dangerouslySetInnerHTML={{ __html: result?.key ?? '' }}
                  />
                  <div className={styles.amount}>
                    {result.amount.toFixed(2)}

                    {result.secondaryAmount !== null && (
                      <>
                        <br />
                        {result.secondaryAmount.toFixed(2)}
                      </>
                    )}
                  </div>
                </li>
                <hr />
              </React.Fragment>
            )
        )}
      </ul>
      {total && (
        <div className={styles.divTotalContainer}>
          <strong>{t('total')}</strong>
          <div className={styles.totalAmount}>
            <h6>U$S</h6>
            <strong>{total}</strong>
          </div>
        </div>
      )}
    </div>
  );
};
